import React from 'react';
import { ImageOff } from 'lucide-react';

/**
 * SEO-ready job gallery. Built to be a drop-in container — once real photos
 * are added to /public/gallery/<service>/ they will appear automatically.
 *
 * Each image expects:
 *   - WebP (browsers auto-fallback to source if missing)
 *   - Descriptive filename: <service>-<job>-<location>.webp
 *   - Detailed alt text: see Plumbing/Handyman pages for examples
 *
 * Props:
 *   serviceSlug: 'plumbing' | 'emergency-plumber' | 'handyman' | 'property-maintenance'
 *   images: optional array — if omitted, renders empty-state
 *   maxColumns: default 3
 */
const Gallery = ({ serviceSlug, images = [], maxColumns = 3, title = 'Recent Work' }) => {
  // Lazy-loading + responsive sizing is set on each <img>. WebP path convention
  // is `/gallery/<serviceSlug>/<filename>` once you upload real photos.
  if (!images.length) {
    return (
      <div
        className="rounded-lg border border-dashed border-gray-800 bg-gray-900/30 p-8 text-center"
        data-testid={`gallery-empty-${serviceSlug}`}
      >
        <ImageOff className="w-8 h-8 text-gray-600 mx-auto mb-3" />
        <h3 className="text-white font-semibold mb-1">{title}</h3>
        <p className="text-gray-400 text-sm max-w-md mx-auto">
          Gallery coming soon. We are currently photographing our latest jobs across Kent, London and Essex
          and will publish them here shortly. In the meantime, call us to discuss your project.
        </p>
      </div>
    );
  }

  const colClass = maxColumns === 2 ? 'sm:grid-cols-2' : maxColumns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section data-testid={`gallery-${serviceSlug}`}>
      <h3 className="text-xl font-bold text-white mb-4">{title}</h3>
      <div className={`grid grid-cols-1 ${colClass} gap-4`}>
        {images.map((img, idx) => (
          <figure
            key={img.src}
            className="group relative overflow-hidden rounded-lg border border-gray-800 bg-gray-900/40"
            data-testid={`gallery-item-${serviceSlug}-${idx}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              decoding="async"
              width={img.width || 800}
              height={img.height || 600}
              className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {img.caption && (
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-950/95 to-transparent p-3 text-sm text-gray-200">
                {img.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
};

export default Gallery;
